import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { CalloutComponent } from '../../../../shared/components/callout/callout.component';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import {
  AI_INSTRUCTIONS_MAX_LENGTH,
  EAiProviderType,
  IAiProvider,
  IAiProviderCredentials,
  TFormAiProvider,
  TFormControlAiProvider,
} from './ai-providers.interface';
import { AiProvidersStore, IAiProviderComponentState } from './ai-providers.store';

@Component({
  imports: [
    CalloutComponent,
    DatatableComponent,
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatDropdown,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    NgClass,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
  ],
  providers: [AiProvidersStore, HttpUtilitiesService],
  selector: 'app-ai-providers',
  standalone: true,
  templateUrl: './ai-providers.component.html',
})
export class AiProvidersComponent
  extends BaseCrudPageComponent<IAiProvider, IAiProviderComponentState>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public datatableConfigurations: IDatatableConfiguration<IAiProvider> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<IAiProvider>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'providerType', isSortable: true },
    { id: 'isActive', isSortable: true },
  ];

  public readonly providerTypeOptions: IDropdownOption<EAiProviderType>[] = Object.values(EAiProviderType).map(
    (providerType: EAiProviderType) => ({
      id: providerType,
      name: this.translate.instant(`page.artificialIntelligence.providerTypes.${providerType}`),
    }),
  );

  public readonly addEditForm: FormGroup<TFormControlAiProvider> = new FormGroup({
    apiKey: new FormControl(''),
    instructions: new FormControl('', [Validators.maxLength(AI_INSTRUCTIONS_MAX_LENGTH)]),
    isActive: new FormControl(true),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
    providerType: new FormControl([] as IDropdownOption<EAiProviderType>[], [Validators.required]),
  });

  constructor(
    public readonly store: AiProvidersStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfAiProvider',
      multiContext: 'field.aiProviders',
      nameProperty: 'name',
      singleContext: 'field.aiProvider',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'providerType', 'instructions', 'isActive'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();
    this.setApiKeyRequired(true);
    this.addEditForm.reset({
      apiKey: '',
      instructions: '',
      isActive: true,
      name: '',
      providerType: this.providerTypeOptions.slice(0, 1),
    });

    this.openFormDialog();
  }

  public initializeEdit(): void {
    const [item] = this.selectedRows();

    if (!item) {
      return;
    }

    this.setupForEdit();
    this.addEditForm.enable();
    this.addEditForm.controls.providerType.disable();
    this.setApiKeyRequired(false);
    this.addEditForm.reset({
      apiKey: '',
      instructions: item.instructions ?? '',
      isActive: item.isActive,
      name: item.name,
      providerType: this.providerTypeOptions.filter((option) => option.id === item.providerType),
    });

    this.openFormDialog();
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const value: TFormAiProvider = this.addEditForm.getRawValue() as TFormAiProvider;
    const apiKey: string = FormUtilitiesService.trimIfString(value.apiKey ?? '');
    const credentials: IAiProviderCredentials | undefined = apiKey ? { apiKey } : undefined;
    const common = {
      instructions: FormUtilitiesService.trimIfString(value.instructions) || null,
      isActive: value.isActive,
      name: FormUtilitiesService.trimIfString(value.name),
    };

    if (!this.isEditDialog()) {
      this.store.createOne({ ...common, credentials: { apiKey }, providerType: value.providerType[0].id });
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: { ...common, ...(credentials ? { credentials } : {}) }, id: item.id });
    }

    this.matDialog.closeAll();
  }

  // Credentials are required on create; on edit an empty field keeps the stored value.
  private setApiKeyRequired(isRequired: boolean): void {
    this.addEditForm.controls.apiKey.setValidators(isRequired ? [Validators.required, TrimmedRequiredValidator()] : []);
    this.addEditForm.controls.apiKey.updateValueAndValidity();
  }

  private openFormDialog(): void {
    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.small,
    });
  }
}
